import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ChevronLeft } from 'lucide-react';
import { needsAccountTypeChoice, useAuthStore } from '../../state/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { supabaseEnabled } from '../../lib/supabase';
import { homeForUserType } from '../../components/layout/RequireUserType';

const PHONE_REGEX = /^\+\d{8,15}$/;

export default function PhoneLoginPage() {
  const navigate = useNavigate();
  const sendPhoneOtp = useAuthStore((s) => s.sendPhoneOtp);
  const verifyPhoneOtp = useAuthStore((s) => s.verifyPhoneOtp);
  const isLoading = useAuthStore((s) => s.isLoading);

  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('+91');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  if (!supabaseEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4">
        <Card className="w-full max-w-md border-gray-800 bg-gray-900">
          <CardContent className="p-8 text-center space-y-3">
            <h2 className="text-lg font-semibold text-gray-100">Phone sign-in is unavailable</h2>
            <p className="text-sm text-gray-400">
              This deployment has not been configured for phone-OTP login. Please use email &amp; password.
            </p>
            <Button onClick={() => navigate('/login')} variant="outline" className="border-gray-700 text-gray-200">
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSend = async (e) => {
    e.preventDefault();
    setError('');
    if (!PHONE_REGEX.test(phone)) {
      setError('Enter a phone number in international format, e.g. +919876543210.');
      return;
    }
    setSending(true);
    const result = await sendPhoneOtp(phone);
    setSending(false);
    if (!result.success) {
      setError(result.error || 'Could not send OTP. Please try again.');
      return;
    }
    setStep('otp');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{4,8}$/.test(otp)) {
      setError('Enter the numeric code from the SMS.');
      return;
    }
    const result = await verifyPhoneOtp(phone, otp.trim());
    if (!result.success) {
      setError(result.error || 'Incorrect or expired code.');
      return;
    }
    const user = useAuthStore.getState().user;
    if (needsAccountTypeChoice(user)) {
      navigate('/choose-account-type', { replace: true });
      return;
    }
    const userType = user?.userType;
    navigate(homeForUserType(userType), { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4">
      <Card className="w-full max-w-md border-gray-800 bg-gray-900">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-2">
            <div className="p-3 rounded-xl bg-emerald-600/20">
              <Activity className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-100">
            {step === 'phone' ? 'Sign in with phone' : 'Enter the code we sent'}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {step === 'phone'
              ? 'We\u2019ll text you a one-time code.'
              : <>Code sent to <span className="text-gray-200">{phone}</span>.</>}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert className="border-red-800 bg-red-950 mb-4">
              <AlertDescription className="text-red-300">{error}</AlertDescription>
            </Alert>
          )}

          {step === 'phone' ? (
            <form onSubmit={handleSend} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Phone number</Label>
                <Input
                  type="tel"
                  inputMode="tel"
                  placeholder="+919876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\s+/g, ''))}
                  required
                  className="bg-gray-800 border-gray-700 text-gray-100"
                />
                <p className="text-xs text-gray-500">
                  Include country code (e.g. <code>+91</code> for India). Standard SMS rates apply.
                </p>
              </div>
              <Button type="submit" disabled={sending || isLoading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {sending ? 'Sending code\u2026' : 'Send code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Verification code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d*"
                  placeholder="6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  required
                  className="bg-gray-800 border-gray-700 text-gray-100 tracking-widest text-center text-lg"
                />
              </div>
              <Button type="submit" disabled={isLoading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {isLoading ? 'Verifying\u2026' : 'Verify &amp; sign in'}
              </Button>
              <button
                type="button"
                onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
                className="w-full text-xs text-gray-400 hover:text-emerald-400 flex items-center justify-center gap-1"
              >
                <ChevronLeft className="w-3 h-3" />
                Use a different number
              </button>
            </form>
          )}

          <div className="text-center text-sm text-gray-400 mt-5">
            Prefer password?{' '}
            <button type="button" onClick={() => navigate('/login')} className="text-emerald-500 hover:underline font-medium">
              Sign in with email
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
